import {packageId} from '@/config';
import {RemoteWebConfig, WebConfigValues, webConfig} from './web-config.state';

const WEB_CONFIG_TIMEOUT_MS = 5000;
const WEB_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
const WEB_CONFIG_FAILURE_RETRY_MS = 15 * 1000;
const WEB_CONFIG_API_PATH = '/api/app/web/config';

export interface ClientBootstrapConfigResult {
  timestamp: string;
  code: number;
  msg?: string | null;
  data: RemoteWebConfig;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const extractRemoteWebConfig = (
  payload: unknown,
): RemoteWebConfig | null => {
  if (!isRecord(payload)) {
    return null;
  }

  const envelope = payload as Partial<ClientBootstrapConfigResult>;
  if (envelope.code !== 0 || !isRecord(envelope.data)) {
    return null;
  }

  return envelope.data as RemoteWebConfig;
};

let inFlightPromise: Promise<Readonly<WebConfigValues>> | null = null;
let lastSuccessAt = 0;
let lastFailureAt = 0;

const now = () => Date.now();

const shouldRefreshWebConfig = (force = false) => {
  if (force) {
    return true;
  }

  if (!lastSuccessAt && !lastFailureAt) {
    return true;
  }

  if (lastSuccessAt) {
    return now() - lastSuccessAt >= WEB_CONFIG_CACHE_TTL_MS;
  }

  return now() - lastFailureAt >= WEB_CONFIG_FAILURE_RETRY_MS;
};

const requestWebConfig = async (): Promise<Readonly<WebConfigValues>> => {
  const controller =
    typeof AbortController === 'undefined' ? null : new AbortController();
  let timeout: number | undefined;

  try {
    const responseResult = await new Promise<{
      response: Response;
      payload: unknown;
    }>((resolve, reject) => {
      timeout = window.setTimeout(() => {
        controller?.abort();
        reject(new Error('Web config request timed out'));
      }, WEB_CONFIG_TIMEOUT_MS);

      fetch(WEB_CONFIG_API_PATH, {
        method: 'POST',
        headers: {
          packageid: String(packageId),
        },
        signal: controller?.signal,
      }).then(response => {
        if (!response.ok) {
          resolve({response, payload: null});
          return;
        }
        response.json().then(payload => resolve({response, payload}), reject);
      }, reject);
    });

    if (!responseResult.response.ok) {
      throw new Error(
        `Web config request failed: ${responseResult.response.status}`,
      );
    }

    const remoteConfig = extractRemoteWebConfig(responseResult.payload);
    if (!remoteConfig) {
      throw new Error('Web config payload is invalid');
    }

    webConfig.applyRemote(remoteConfig);
    lastSuccessAt = now();
    lastFailureAt = 0;
    return webConfig.snapshot();
  } catch (error) {
    lastFailureAt = now();
    throw error;
  } finally {
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
    }
  }
};

/** 启动期优先加载；成功按 TTL 刷新，失败按退避时间重试。 */
export const loadWebConfig = ({force = false}: {force?: boolean} = {}) => {
  if (typeof window === 'undefined') {
    return Promise.resolve(webConfig.snapshot());
  }

  if (!shouldRefreshWebConfig(force)) {
    return Promise.resolve(webConfig.snapshot());
  }

  if (!inFlightPromise) {
    inFlightPromise = requestWebConfig()
      .catch(() => webConfig.snapshot())
      .finally(() => {
        inFlightPromise = null;
      });
  }

  return inFlightPromise;
};
